import { formatBondDate } from "@/lib/bondValueDisplay";
import {
  formatInvoiceAmount,
  readInvoiceStatus,
  type InvoiceRow,
} from "@/lib/invoices";

// BRK-S426, BRK-S502 — the submission page's Billing tab: which of the
// account's invoices bill THIS order, and the wireframe's four tiles above
// them (Premium billed · Paid · Balance due · Next payment due).
//
// There is no order-scoped invoice read: the account's
// (`GET /api/broker/accounts/{id}/invoices`, PMS-S507, pinned) is filtered here
// by `bondSubmissionId`. The tiles add up ONLY those served rows — Premium
// billed is every row, Paid the `paid` ones, Balance due the `due` / `overdue`
// / `awaiting_decision` ones, Next payment due the earliest open one's due
// date. Until the rows are read, or when a row cannot be read, a figure is
// "—"; a read with NO invoices is the wireframe's "$0 · No invoices yet" —
// the server said nothing is billed.
//
// Nothing about payment beyond the broker-safe status (D5, BRK-S187).

/** The account's invoice rows that bill this order. */
export function orderInvoices(
  rows: readonly InvoiceRow[],
  orderId: string,
): InvoiceRow[] {
  return rows.filter((r) => r.bondSubmissionId === orderId);
}

export type SubmissionBillingTotals =
  /** The rows are not read (loading, or the read failed). */
  | { state: "unread" }
  /** Nothing has been invoiced on this order. */
  | { state: "none" }
  /** A row carries a status or amount this build can't read, so any sum
   *  would leave it out and understate the order. */
  | { state: "unreadable" }
  | {
      state: "summed";
      invoiced: number;
      paid: number;
      outstanding: number;
    };

const OUTSTANDING = new Set(["due", "overdue", "awaiting_decision"]);

/** Cents, so 0.1 + 0.2 reads $0.30 rather than a double's remainder. */
const cents = (n: number) => Math.round(n * 100) / 100;

export function submissionBillingTotals(
  rows: readonly InvoiceRow[] | null,
): SubmissionBillingTotals {
  if (rows === null) return { state: "unread" };
  if (rows.length === 0) return { state: "none" };
  let invoiced = 0;
  let paid = 0;
  let outstanding = 0;
  for (const r of rows) {
    const status = readInvoiceStatus(r.status);
    // A formatted string is refused, never parsed (D-10).
    const amount: unknown = r.amount;
    if (
      status === null ||
      typeof amount !== "number" ||
      !Number.isFinite(amount)
    ) {
      return { state: "unreadable" };
    }
    invoiced += amount;
    if (status === "paid") paid += amount;
    else if (OUTSTANDING.has(status)) outstanding += amount;
  }
  return {
    state: "summed",
    invoiced: cents(invoiced),
    paid: cents(paid),
    outstanding: cents(outstanding),
  };
}

/** One tile: the figure and the line under it. */
export interface BillingTile {
  label: "Premium billed" | "Paid" | "Balance due" | "Next payment due";
  value: string;
  caption: string;
}

const DASH = "—";
const money = formatInvoiceAmount;
const plural = (n: number, one: string, many: string) =>
  `${String(n)} ${n === 1 ? one : many}`;

/** The four tiles (the October wireframe's Billing tab). */
export function submissionBillingTiles(
  rows: readonly InvoiceRow[] | null,
): BillingTile[] {
  const totals = submissionBillingTotals(rows);
  if (totals.state === "none") {
    return [
      { label: "Premium billed", value: money(0), caption: "No invoices yet" },
      { label: "Paid", value: money(0), caption: DASH },
      { label: "Balance due", value: money(0), caption: "Nothing due" },
      {
        label: "Next payment due",
        value: DASH,
        caption: "No upcoming payments",
      },
    ];
  }
  if (totals.state !== "summed" || rows === null) {
    return (
      ["Premium billed", "Paid", "Balance due", "Next payment due"] as const
    ).map((label) => ({ label, value: DASH, caption: DASH }));
  }
  const paid = rows.filter((r) => readInvoiceStatus(r.status) === "paid");
  const open = rows
    .filter((r) => OUTSTANDING.has(readInvoiceStatus(r.status) ?? ""))
    .sort((a, b) => a.dueOn.localeCompare(b.dueOn));
  const next = open[0];
  return [
    {
      label: "Premium billed",
      value: money(totals.invoiced),
      caption: plural(rows.length, "invoice", "invoices"),
    },
    {
      label: "Paid",
      value: money(totals.paid),
      caption: paid.length === 0 ? DASH : plural(paid.length, "paid", "paid"),
    },
    {
      label: "Balance due",
      value: money(totals.outstanding),
      caption:
        open.length === 0 ? "Nothing due" : plural(open.length, "open", "open"),
    },
    next === undefined
      ? {
          label: "Next payment due",
          value: DASH,
          caption: "No upcoming payments",
        }
      : {
          label: "Next payment due",
          value: formatBondDate(next.dueOn),
          caption: typeof next.amount === "number" ? money(next.amount) : DASH,
        },
  ];
}
